// ---------------------------------------------------------------------------
// FactorSettingsPanel — shared factor settings UI
//
// Used by TrustLinkSettingsModalV2 and NewTrustLinkModal.
// Visual design mirrors ContactDetailModal: same accordion bars, 3-col grid,
// and single action-button per cell that opens an inline GrantForm-style form.
//
// Layout:
//   1. Profile cards — You + Them side by side
//   2. Column headers — "WHAT I SHARE" | "WHAT I ASK"
//   3. BASIC accordion section  (starts expanded)
//   4. ENHANCED accordion section (starts collapsed)
//   5. Legend strip
//
// Row grid: grid-cols-[60px_1fr_1fr]
//   Col 1 — icon + short label
//   Col 2 — my share state button → opens Hide | Verified | Detailed form
//   Col 3 — what I ask state button → opens same form
// ---------------------------------------------------------------------------

import { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types (re-exported for modal consumers)
// ---------------------------------------------------------------------------

export type DataShareMode = "hide" | "valid" | "share";

export interface FactorSetting {
  key: string;
  myMode: DataShareMode;
  theirMode: DataShareMode;
}

export interface TrustLinkSettingsV2 {
  linkName: string;
  factors: FactorSetting[];
}

export interface MyFactorData {
  phone?: string;
  email?: string;
  /** Per-factor KYC completion. key = factor key. undefined = unknown. */
  kyc?: Partial<Record<string, boolean>>;
}

// ---------------------------------------------------------------------------
// Factor catalogue
// ---------------------------------------------------------------------------

export interface FactorDef {
  key: string;
  icon: string;
  label: string;
  short: string;
  noShare?: true;
  /** KYC-only factor — backend ignores in field_settings. "What I Share" is not applicable. */
  kycOnly?: true;
}

export const BASIC_FACTORS: FactorDef[] = [
  { key: "phone",    icon: "smartphone", label: "Phone Number",  short: "Phone"  },
  { key: "email",    icon: "mail",       label: "Email Address", short: "Email"  },
  { key: "gov_id",   icon: "id_card",    label: "Gov ID",        short: "Gov ID" },
  { key: "liveness", icon: "face",       label: "Liveness",      short: "Live",  kycOnly: true },
];

export const ENHANCED_FACTORS: FactorDef[] = [
  { key: "ssn",           icon: "lock",        label: "SSN",             short: "SSN",     noShare: true },
  { key: "address",       icon: "home",        label: "Physical Address", short: "Addr."                 },
  { key: "ofac",          icon: "policy",      label: "OFAC",             short: "OFAC",    kycOnly: true },
  { key: "adverse_media", icon: "newspaper",   label: "Adverse Media",    short: "Media",   kycOnly: true },
  { key: "signature",     icon: "draw",        label: "Signature",        short: "Sign.",   kycOnly: true },
  { key: "dl",            icon: "credit_card", label: "Driver's License", short: "License", kycOnly: true },
];

const ALL_FACTORS = [...BASIC_FACTORS, ...ENHANCED_FACTORS];
const DEF_MAP = Object.fromEntries(ALL_FACTORS.map((d) => [d.key, d]));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function buildDefaultFactors(): FactorSetting[] {
  return ALL_FACTORS.map(({ key }) => ({ key, myMode: "hide", theirMode: "hide" }));
}

export function createDefaultSettingsV2(
  initial?: Partial<TrustLinkSettingsV2>,
): TrustLinkSettingsV2 {
  return {
    linkName: initial?.linkName ?? "",
    factors:  initial?.factors  ?? buildDefaultFactors(),
  };
}

const VALID_LABELS: Record<string, string> = {
  phone: "Phone Verified",  email: "Email Verified",       gov_id: "ID Verified",
  liveness: "Liveness OK",  ssn: "SSN Verified",           address: "Address Verified",
  signature: "Signature",   dl: "License Verified",        ofac: "OFAC Clear",
  adverse_media: "Media Clear",
};

// ---------------------------------------------------------------------------
// ActionBtn — pill style (matches ContactDetailModal)
// ---------------------------------------------------------------------------

function ActionBtn({
  children, variant = "default", onClick, disabled,
}: {
  children: React.ReactNode;
  variant?: "default" | "primary" | "request";
  onClick?: () => void;
  disabled?: boolean;
}) {
  const base = "shrink-0 rounded-full px-3.5 py-1 text-xs font-semibold transition-colors focus:outline-none disabled:opacity-50 min-w-[60px] text-center";
  const variants: Record<string, string> = {
    default: "border border-gray-200 bg-white text-text-secondary hover:bg-gray-50",
    primary: "bg-primary-500 text-white hover:bg-primary-600",
    request: "border border-primary-500 text-primary-600 bg-transparent hover:bg-primary-50",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={cn(base, variants[variant])}>
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// InlinePreview — shown below name in profile cards
// ---------------------------------------------------------------------------

function InlinePreview({
  factorKey, mode, side, myData,
}: {
  factorKey: string; mode: DataShareMode; side: "my" | "their"; myData?: MyFactorData;
}) {
  if (mode === "hide") return null;

  if (mode === "valid") {
    const label = VALID_LABELS[factorKey] ?? "Verified";
    if (side === "my") {
      return (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600">
          <Icon name="check_circle" filled style={{ fontSize: 11 }} />
          {label}
        </span>
      );
    }
    return (
      <span className="text-[10px] font-medium text-orange-500">
        Awaiting Verification
      </span>
    );
  }

  // share / detailed mode
  if (side === "my") {
    if (factorKey === "phone" && myData?.phone)
      return <span className="text-[10px] font-mono text-text-secondary">{myData.phone}</span>;
    if (factorKey === "email" && myData?.email)
      return <span className="text-[10px] font-mono text-text-secondary truncate max-w-[130px]">{myData.email}</span>;
    return <span className="text-[10px] font-semibold text-primary-600">View →</span>;
  }

  const PLACEHOLDERS: Record<string, string> = {
    phone: "(555) 000-0000", email: "their@email.com",
    ssn: "•••-••-####",     address: "Their address",
  };
  const val = PLACEHOLDERS[factorKey];
  return val
    ? <span className="text-[10px] font-mono text-text-muted">{val}</span>
    : <span className="text-[10px] text-text-muted">View →</span>;
}

// ---------------------------------------------------------------------------
// Avatar helpers (matches ContactDetailModal)
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-600",    "bg-emerald-100 text-emerald-600",
  "bg-amber-100 text-amber-600",  "bg-rose-100 text-rose-600",
  "bg-purple-100 text-purple-600","bg-cyan-100 text-cyan-600",
];

function getAvatarColor(initials: string) {
  const idx = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function AvatarCircle({
  name, initials, avatarUrl, size = "sm",
}: {
  name: string; initials: string; avatarUrl?: string | null; size?: "sm" | "lg";
}) {
  const [imgErr, setImgErr] = useState(false);
  const color = getAvatarColor(initials);
  const sizeClass = size === "lg" ? "h-16 w-16 text-base" : "h-12 w-12 text-sm";
  if (avatarUrl && !imgErr) {
    return (
      <img src={avatarUrl} alt={name}
        className={cn(sizeClass, "rounded-full border-4 border-primary-200 object-cover")}
        onError={() => setImgErr(true)}
      />
    );
  }
  return (
    <div className={cn(sizeClass, "flex items-center justify-center rounded-full border-4 border-primary-200 font-black", color)}>
      {initials || "?"}
    </div>
  );
}

// ---------------------------------------------------------------------------
// YouCard — profile card for the "me" side
// ---------------------------------------------------------------------------

function YouCard({
  myName, myInitials, myAvatarUrl, factors, myData, size = "sm",
}: {
  myName: string; myInitials: string; myAvatarUrl?: string | null;
  factors: FactorSetting[]; myData?: MyFactorData; size?: "sm" | "lg";
}) {
  const active = factors.filter((f) => f.myMode !== "hide");
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-2.5 py-2.5 shadow-sm h-full">
      <AvatarCircle name={myName} initials={myInitials} avatarUrl={myAvatarUrl} size={size} />
      <div className="text-center">
        <p className="text-xs font-bold text-text-primary leading-tight truncate w-full">{myName}</p>
        <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted">You</p>
      </div>
      {active.length > 0 && (
        <div className="w-full space-y-1">
          {active.map((f) => {
            const def = DEF_MAP[f.key];
            if (!def) return null;
            return (
              <div key={f.key} className="flex items-center gap-1">
                <Icon name={def.icon} style={{ fontSize: 11 }} className="text-text-muted shrink-0" />
                <InlinePreview factorKey={f.key} mode={f.myMode} side="my" myData={myData} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ThemCard — placeholder card for "them" (generic contact)
// ---------------------------------------------------------------------------

function ThemCard({ factors, size = "sm" }: { factors: FactorSetting[]; size?: "sm" | "lg" }) {
  const active = factors.filter((f) => f.theirMode !== "hide");
  const avatarSize = size === "lg" ? "h-16 w-16 text-3xl" : "h-12 w-12 text-2xl";
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-2.5 py-2.5 shadow-sm h-full">
      <div className={cn("flex items-center justify-center rounded-full border-4 border-dashed border-gray-300 bg-gray-50", avatarSize)}>
        <Icon name="person" className={cn(size === "lg" ? "text-3xl" : "text-2xl", "text-gray-300")} />
      </div>
      <div className="text-center">
        <p className="text-xs font-bold text-text-muted">Contact</p>
        <p className="text-[10px] text-text-muted leading-tight">Anyone with your link</p>
        <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted">Them</p>
      </div>
      {active.length > 0 && (
        <div className="w-full space-y-1">
          {active.map((f) => {
            const def = DEF_MAP[f.key];
            if (!def) return null;
            return (
              <div key={f.key} className="flex items-center gap-1">
                <Icon name={def.icon} style={{ fontSize: 11 }} className="text-text-muted shrink-0" />
                <InlinePreview factorKey={f.key} mode={f.theirMode} side="their" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MyCellContent — displays current "What I Share" state for a factor
// ---------------------------------------------------------------------------

function MyCellContent({
  mode, locked, onClick,
}: {
  mode: DataShareMode; locked: boolean; onClick: () => void;
}) {
  if (locked) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <ActionBtn variant="primary" disabled>Share</ActionBtn>
        <Link to="/settings"
          className="text-[9px] text-primary-500 hover:underline"
          onClick={(e) => e.stopPropagation()}>
          Add Verification first
        </Link>
      </div>
    );
  }
  if (mode === "valid") {
    return (
      <button type="button" onClick={onClick}
        className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors focus:outline-none">
        Verified <Icon name="check_circle" filled style={{ fontSize: 11 }} />
      </button>
    );
  }
  if (mode === "share") {
    return (
      <button type="button" onClick={onClick}
        className="inline-flex items-center gap-0.5 rounded-md bg-primary-50 border border-primary-200 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700 hover:bg-primary-100 transition-colors focus:outline-none">
        Detailed <Icon name="info" style={{ fontSize: 11 }} />
      </button>
    );
  }
  // hide → Share button (like ContactDetailModal not_requested state)
  return <ActionBtn variant="primary" onClick={onClick}>Share</ActionBtn>;
}

// ---------------------------------------------------------------------------
// TheirCellContent — displays current "What I Ask" state for a factor
// ---------------------------------------------------------------------------

function TheirCellContent({
  mode, def, onClick,
}: {
  mode: DataShareMode; def: FactorDef; onClick: () => void;
}) {
  if (mode === "valid") {
    return (
      <button type="button" onClick={onClick}
        className="flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors focus:outline-none">
        <Icon name={def.icon} style={{ fontSize: 13 }} className="shrink-0" />
        Required
      </button>
    );
  }
  if (mode === "share") {
    return (
      <button type="button" onClick={onClick}
        className="flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors focus:outline-none">
        <Icon name={def.icon} style={{ fontSize: 13 }} className="shrink-0" />
        Detailed
      </button>
    );
  }
  // hide → Request button (like ContactDetailModal not_requested their cell)
  return <ActionBtn variant="request" onClick={onClick}>Request</ActionBtn>;
}

// ---------------------------------------------------------------------------
// OptionsForm — matches ContactDetailModal GrantForm layout exactly:
//   [Hide] [Verified] [Detailed]  ·  ml-auto  ·  [Cancel]
// Rendered inside an absolute panel — does not push rows down.
// ---------------------------------------------------------------------------

function OptionsForm({
  mode, noShare, onSelect, onCancel,
}: {
  mode: DataShareMode; noShare?: boolean;
  onSelect: (m: DataShareMode) => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {/* Mode options — left side */}
      <button type="button" onClick={() => onSelect("hide")}
        className={cn(
          "rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
          mode === "hide"
            ? "bg-white text-text-secondary shadow border border-gray-300"
            : "border border-gray-200 bg-white text-text-secondary hover:border-gray-400",
        )}>
        Hide
      </button>
      <button type="button" onClick={() => onSelect("valid")}
        className={cn(
          "rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
          mode === "valid"
            ? "bg-emerald-600 text-white"
            : "border border-gray-200 bg-white text-text-secondary hover:border-emerald-300",
        )}>
        Verified
      </button>
      {!noShare && (
        <button type="button" onClick={() => onSelect("share")}
          className={cn(
            "rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
            mode === "share"
              ? "bg-primary-500 text-white"
              : "border border-gray-200 bg-white text-text-secondary hover:border-primary-300",
          )}>
          Detailed
        </button>
      )}
      {/* Cancel — right side (ml-auto matches ContactDetailModal GrantForm) */}
      <div className="ml-auto flex gap-1.5">
        <button type="button" onClick={onCancel}
          className="rounded-full border border-gray-200 bg-white px-3 py-0.5 text-[10px] font-semibold text-text-secondary hover:bg-gray-50 focus:outline-none">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionHeader — full-width accordion bar (matches ContactDetailModal style)
// ---------------------------------------------------------------------------

function SectionHeader({
  title, isOpen, onToggle,
}: {
  title: string; isOpen: boolean; onToggle: () => void;
}) {
  return (
    <button type="button" onClick={onToggle}
      className="w-full flex items-center justify-between border-y border-gray-200 bg-gray-50/80 px-4 py-2.5 hover:bg-gray-100/80 transition-colors focus:outline-none">
      <span className="text-[11px] font-black uppercase tracking-widest text-text-muted">{title}</span>
      <Icon name={isOpen ? "expand_less" : "expand_more"} className="text-base text-text-muted" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// FactorRow — 3-col grid row + inline options form
// ---------------------------------------------------------------------------

function FactorRow({
  def, setting, onChange, isLast, myVerified,
}: {
  def: FactorDef; setting: FactorSetting;
  onChange: (key: string, side: "my" | "their", mode: DataShareMode) => void;
  isLast: boolean; myVerified?: boolean;
}) {
  const [openSide, setOpenSide] = useState<"my" | "their" | null>(null);
  const locked = myVerified === false;

  function toggle(side: "my" | "their") {
    if (side === "my" && locked) return;
    setOpenSide((prev) => (prev === side ? null : side));
  }

  return (
    <div className={cn(
      "grid grid-cols-[60px_1fr_1fr_60px]",
      !isLast && "border-b border-gray-100",
    )}>
      {/* Col 1: icon + short label */}
      <div className="flex flex-col items-center justify-center gap-0.5 px-1 py-3 border-r border-gray-100">
        <Icon name={def.icon} className="text-base text-text-muted" />
        <span className="text-[9px] font-medium text-text-muted text-center leading-none">{def.short}</span>
      </div>

      {/* Col 2: What I Share — disabled for kycOnly fields (backend ignores them in field_settings) */}
      <div className="relative flex items-center justify-center py-3 border-r border-gray-100">
        {def.kycOnly ? (
          <span className="text-xs text-text-muted">—</span>
        ) : (
          <>
            <MyCellContent
              mode={setting.myMode} locked={locked}
              onClick={() => toggle("my")}
            />
            {openSide === "my" && (
              <div className="absolute left-0 top-full z-20 w-full border border-primary-100 bg-white shadow-lg rounded-b-lg px-3 py-2">
                <OptionsForm
                  mode={setting.myMode}
                  noShare={def.noShare}
                  onSelect={(m) => { onChange(def.key, "my", m); setOpenSide(null); }}
                  onCancel={() => setOpenSide(null)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Col 3: What I Ask — relative so form anchors here */}
      <div className={cn(
        "relative flex items-center justify-center py-3 bg-gray-50 border-r border-gray-100",
        openSide === "their" && "bg-primary-50/40",
      )}>
        <TheirCellContent
          mode={setting.theirMode} def={def}
          onClick={() => toggle("their")}
        />
        {openSide === "their" && (
          <div className="absolute left-0 top-full z-20 w-full border border-primary-100 bg-white shadow-lg rounded-b-lg px-3 py-2">
            <OptionsForm
              mode={setting.theirMode}
              noShare={def.noShare}
              onSelect={(m) => { onChange(def.key, "their", m); setOpenSide(null); }}
              onCancel={() => setOpenSide(null)}
            />
          </div>
        )}
      </div>

      {/* Col 4: right spacer — mirrors left 60px icon col for balanced margins */}
      <div className="bg-gray-50" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// MobileFactorRow — single-column row for the mobile tab view
// ---------------------------------------------------------------------------

function MobileFactorRow({
  def, setting, side, onChange, isLast, myVerified,
}: {
  def: FactorDef; setting: FactorSetting; side: "my" | "their";
  onChange: (key: string, side: "my" | "their", mode: DataShareMode) => void;
  isLast: boolean; myVerified?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const mode = side === "my" ? setting.myMode : setting.theirMode;
  const locked = side === "my" && myVerified === false;

  function toggle() {
    if (locked) return;
    if (side === "my" && def.kycOnly) return;
    setOpen((o) => !o);
  }

  return (
    <div className={cn("relative", !isLast && "border-b border-gray-100")}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Icon + full label */}
        <Icon name={def.icon} className="text-base text-text-muted shrink-0" />
        <span className="flex-1 text-sm font-medium text-text-primary">{def.label}</span>
        {/* Action button */}
        {side === "my" && def.kycOnly
          ? <span className="text-xs text-text-muted">—</span>
          : side === "my"
            ? <MyCellContent mode={mode} locked={locked} onClick={toggle} />
            : <TheirCellContent mode={mode} def={def} onClick={toggle} />
        }
      </div>
      {/* Inline options — absolute below entire row */}
      {open && (
        <div className="absolute left-0 top-full z-20 w-full border border-primary-100 bg-white shadow-lg rounded-b-lg px-3 py-2">
          <OptionsForm
            mode={mode}
            noShare={def.noShare}
            onSelect={(m) => { onChange(def.key, side, m); setOpen(false); }}
            onCancel={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FactorSettingsPanel — the full scrollable body
// ---------------------------------------------------------------------------

interface FactorSettingsPanelProps {
  settings: TrustLinkSettingsV2;
  onChange: (key: string, side: "my" | "their", mode: DataShareMode) => void;
  /** Optional — retained for backward compat with parent modals but not called */
  onBulkChange?: (defs: FactorDef[], side: "my" | "their", mode: DataShareMode) => void;
  myName: string;
  myInitials: string;
  myAvatarUrl?: string | null;
  myData?: MyFactorData;
}

export default function FactorSettingsPanel({
  settings, onChange,
  myName, myInitials, myAvatarUrl, myData,
}: FactorSettingsPanelProps) {
  const [basicOpen,    setBasicOpen]    = useState(true);
  const [enhancedOpen, setEnhancedOpen] = useState(false);
  const [activeTab,    setActiveTab]    = useState<"my" | "their">("their");

  const factorMap  = Object.fromEntries(settings.factors.map((f) => [f.key, f]));
  const getSetting = (key: string): FactorSetting =>
    factorMap[key] ?? { key, myMode: "hide", theirMode: "hide" };
  const myKyc = myData?.kyc;

  return (
    <div>
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT  (md:hidden)
          Tab bar → active card → single-column factor rows per section
          ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* Tab bar */}
        <div className="flex border-b border-gray-200 bg-white sticky top-0 z-10">
          <button
            type="button"
            onClick={() => setActiveTab("their")}
            className={cn(
              "flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors focus:outline-none",
              activeTab === "their"
                ? "text-text-primary border-b-2 border-gray-500"
                : "text-text-muted hover:text-text-secondary",
            )}>
            What I Ask
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("my")}
            className={cn(
              "flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors focus:outline-none",
              activeTab === "my"
                ? "text-primary-600 border-b-2 border-primary-500"
                : "text-text-muted hover:text-text-secondary",
            )}>
            What I Share
          </button>
        </div>

        {/* Active card — full width, lg avatar */}
        <div className="px-4 pt-4 pb-3 bg-gray-50 border-b border-gray-200">
          {activeTab === "my" ? (
            <YouCard
              myName={myName} myInitials={myInitials} myAvatarUrl={myAvatarUrl}
              factors={settings.factors} myData={myData} size="lg"
            />
          ) : (
            <ThemCard factors={settings.factors} size="lg" />
          )}
        </div>

        {/* BASIC section */}
        <SectionHeader title="Basic" isOpen={basicOpen} onToggle={() => setBasicOpen((o) => !o)} />
        {basicOpen && BASIC_FACTORS.map((def, i) => (
          <MobileFactorRow
            key={def.key} def={def} setting={getSetting(def.key)}
            side={activeTab} onChange={onChange}
            isLast={i === BASIC_FACTORS.length - 1}
            myVerified={myKyc ? myKyc[def.key] : undefined}
          />
        ))}

        {/* ENHANCED section */}
        <SectionHeader title="Enhanced" isOpen={enhancedOpen} onToggle={() => setEnhancedOpen((o) => !o)} />
        {enhancedOpen && ENHANCED_FACTORS.map((def, i) => (
          <MobileFactorRow
            key={def.key} def={def} setting={getSetting(def.key)}
            side={activeTab} onChange={onChange}
            isLast={i === ENHANCED_FACTORS.length - 1}
            myVerified={myKyc ? myKyc[def.key] : undefined}
          />
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT  (hidden md:block)
          Side-by-side cards + 4-col grid rows — unchanged from before
          ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        {/* Profile cards */}
        <div className="flex items-stretch gap-3 px-4 pt-4 pb-3 border-b border-gray-200 bg-gray-50">
          <div className="flex-1">
            <YouCard
              myName={myName} myInitials={myInitials} myAvatarUrl={myAvatarUrl}
              factors={settings.factors} myData={myData}
            />
          </div>
          <div className="flex-1">
            <ThemCard factors={settings.factors} />
          </div>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-[60px_1fr_1fr_60px] border-b border-gray-200 sticky top-0 z-10 bg-white">
          <div className="border-r border-gray-200" />
          <div className="py-2 text-center text-[10px] font-bold uppercase tracking-wider text-primary-500 border-r border-gray-200">
            What I Share
          </div>
          <div className="py-2 text-center text-[10px] font-bold uppercase tracking-wider text-text-muted bg-gray-50 border-r border-gray-200">
            What I Ask
          </div>
          <div className="bg-gray-50" />
        </div>

        {/* BASIC section */}
        <SectionHeader title="Basic" isOpen={basicOpen} onToggle={() => setBasicOpen((o) => !o)} />
        {basicOpen && BASIC_FACTORS.map((def, i) => (
          <FactorRow
            key={def.key} def={def} setting={getSetting(def.key)}
            onChange={onChange} isLast={i === BASIC_FACTORS.length - 1}
            myVerified={myKyc ? myKyc[def.key] : undefined}
          />
        ))}

        {/* ENHANCED section */}
        <SectionHeader title="Enhanced" isOpen={enhancedOpen} onToggle={() => setEnhancedOpen((o) => !o)} />
        {enhancedOpen && ENHANCED_FACTORS.map((def, i) => (
          <FactorRow
            key={def.key} def={def} setting={getSetting(def.key)}
            onChange={onChange} isLast={i === ENHANCED_FACTORS.length - 1}
            myVerified={myKyc ? myKyc[def.key] : undefined}
          />
        ))}
      </div>

      {/* ── Legend — outside both layout wrappers, always visible ────── */}
      <div className="border-t border-gray-100 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <span className="text-[10px] font-black uppercase tracking-wider text-text-muted">Legend</span>
          <span className="text-[10px] text-text-secondary">
            <span className="font-bold text-text-primary">Hide</span> — not shared
          </span>
          <span className="text-[10px] text-text-secondary">
            <span className="font-bold text-emerald-600">Verified</span> — badge only
          </span>
          <span className="text-[10px] text-text-secondary">
            <span className="font-bold text-primary-600">Detailed</span> — actual value revealed
          </span>
        </div>
      </div>
    </div>
  );
}
